import React from 'react';
import './style.scss';
import { TreeType } from './model';

import CheckBox from '../../assets/icons/check_box_unchecked.svg';
import SelectedCheckBox from '../../assets/icons/check_box_checked.svg';
import RightArrowIcon from '../../assets/icons/right-arrow.svg';

export function Tree(props: {
  data: TreeType[];
  isParentSelected?: boolean;
  level: number;
  parentId: number;
  notifyParent?: (selectedNodes: { id: number; name: string }[]) => void;
  title: string;
  removeParticularNode: number;
}) {
  const [openedSubtreeIds, setOpenSubTreeIds] = React.useState<Map<number, boolean>>(
    new Map()
  );
  /**
   * This state marks the selected nodes on current level
   */
  const [selectedNodes, setSelectedNodes] = React.useState<Map<number, boolean>>(
    new Map()
  );

  /**
   * This state marks the all selected nodes on whole tree level.
   * For level === 0, which is root node, this state has significance.
   * But for level > 0, this state has not value and will contain empty array.
   */
  const [allSelectedNodes, setAllSelectedNodes] = React.useState<
    Map<number, { id: number; name: string }>
  >(new Map());

  function toggleChildNodeVisilibility(id: number) {
    setOpenSubTreeIds(prevValue =>
      new Map(prevValue.entries()).set(id, !prevValue.get(id))
    );
  }

  function toggleNodeSelection(id: number) {
    setSelectedNodes(prevValue =>
      new Map(prevValue.entries()).set(id, !prevValue.get(id))
    );
  }

  /**
   * Check for mutation on Tree at Level === 0, i.e Root
   */
  React.useEffect(() => {
    let mutationObserver: null | MutationObserver = null;

    if (props.level === 0) {
      mutationObserver = new MutationObserver(function(mutations) {
        for (let i = 0; i < mutations.length; i += 1) {
          if (mutations[i].type === 'attributes') {
            const target = mutations[i].target as HTMLDivElement;
            if (target) {
              const id = Number(target.getAttribute('data-id'));
              const name = target.getAttribute('data-name') || '';
              const dataSelected = target.getAttribute('data-selected');
              if (dataSelected) {
                const dataSelectedParsed = JSON.parse(dataSelected);
                /**
                 * Keep Selected nodes in Map.
                 * Delete unselected nodes, which was previously selected
                 */
                setAllSelectedNodes(prevValue => {
                  const tempMap = new Map(prevValue.entries());
                  if (dataSelectedParsed) {
                    tempMap.set(id, { id, name });
                  } else {
                    tempMap.delete(id);
                  }
                  return tempMap;
                });
              }
            }
          }
        }
      });
      const rootNode = document.querySelector('.tree-node-0');
      if (rootNode) {
        mutationObserver.observe(rootNode, {
          attributes: true,
          childList: true,
          subtree: true,
          attributeFilter: ['data-selected'],
        });
      }
    }

    return () => {
      if (mutationObserver !== null) {
        mutationObserver.disconnect();
      }
    };
  }, []);

  /**
   * Check for change in allSelectedNodes at level === 0. i.e Root
   */
  React.useEffect(() => {
    if (props.level === 0 && props.notifyParent) {
      props.notifyParent(Array.from(allSelectedNodes.values()));
    }
  }, [allSelectedNodes, props.level]);

  const fetchData = async () => {
    const data = props.data;
    if (data.length > 0) {
      data.map(val => {
        if (val.queueName === props.title) {
          let id = val.id;
          setSelectedNodes(prevValue =>
            new Map(prevValue.entries()).set(id, !prevValue.get(id))
          );
        }
      });
    }
  };
  React.useEffect(() => {
    if (props.data.length > 0) {
      fetchData();
    }
  }, [props.data]);

  React.useEffect(() => {
    if (props.title && props.data.length > 0) {
      for (let i = 0; i < props.data.length; i += 1) {
        if (props.data[i].children.length > 0) {
          toggleChildNodeVisilibility(props.data[i].id);
        }
      }
    }
  }, [props.title, props.data]);
  React.useEffect(() => {
    if (props.removeParticularNode) {
      setSelectedNodes(prevValue => {
        const tmp = new Map(prevValue.entries());
        tmp.delete(props.removeParticularNode);
        return tmp;
      });
      setAllSelectedNodes(prevValue => {
        const tmp = new Map(prevValue.entries());
        tmp.delete(props.removeParticularNode);
        return tmp;
      });
    }
  }, [props.removeParticularNode]);
  return (
    <ul className={`tree-node-${props.level} ${props.level > 0 && 'tree-border'}`}>
      {props.data.map(eachNode => {
        const arrowIconClassList = ['clickable'];

        if (eachNode.children.length > 0) {
          arrowIconClassList.push('visible');
        } else {
          arrowIconClassList.push('hidden');
        }

        if (openedSubtreeIds.get(eachNode.id)) {
          arrowIconClassList.push('rotate');
        }

        return (
          <li key={eachNode.id}>
            <span
              className='node-title'
              data-id={eachNode.id}
              data-name={eachNode.entityName}
              data-selected={selectedNodes.get(eachNode.id)}>
              <img
                src={selectedNodes.get(eachNode.id) ? SelectedCheckBox : CheckBox}
                onClick={() => {
                  toggleNodeSelection(eachNode.id);
                }}
                className={`clickable ${props.title === 'Default' && 'disable-events'}`}
              />
              <img
                src={RightArrowIcon}
                alt='arrow'
                className={arrowIconClassList.join(' ')}
                onClick={() => toggleChildNodeVisilibility(eachNode.id)}
                style={{ width: '22px', height: '22px', opacity: '2.5' }}
              />
              <span>{eachNode.entityName || 'Entity Name not coming from API'}</span>
            </span>
            {openedSubtreeIds.get(eachNode.id) ? (
              <Tree
                data={eachNode.children}
                isParentSelected={selectedNodes.get(eachNode.id)}
                level={props.level + 1}
                /**
                 * This node's id will be parentid
                 * for its children
                 */
                parentId={eachNode.id}
                title={props.title}
                removeParticularNode={props.removeParticularNode}
              />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
